 
import React, { useCallback, useMemo } from 'react';
import { Box, capitalizeFirstLetter,Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { FieldStateKey, NON_ASSIGNABLE_VALUE, PermissionKey,REQUIRED_TEXT } from '@/common/constants';
import { AccountToPushToField } from '@/components/elements/AccountToPushToField';
import { AccountMethods, AccountUsageValues, daysOfWeek,FrequencyOptions } from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/AccountUsage/AccountUsage.types';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks/useSetupEmployerMode';
import { BankInfo } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';
import { BankMethod } from '@/modules/employer/hooks/useBankMethods.query';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

const CUSTOM_DAY_FIELD_HINT = 'If a month has fewer days than the entered number, replenishment will be processed on the last day of this month.';
const CUSTOM_DAY_ERROR_TEXT = 'This number does not match the range of available numbers (1-31)';
const ACCOUNT_PLACEHOLDER = 'The account used to push funds for replenishment will be provided once organization is activated.';

const placeholder = 'Select bank account';
const weeklyOptions = Object.keys(daysOfWeek);

const automatedOrgLockOptions = [
  { label: 'Enable', value: true },
  { label: 'Disable', value: false },
];

const useFundingReplenishmentFields = (
  banks: BankInfo[],
  methods: BankMethod[],
  formattedData: EmployerDto,
  currentValues?: AccountUsageValues,
  activateMode?: boolean,
  isLoading?: boolean,
  notionalAccount?: string,
  routingNumber?: string,
): Field[] => {
  const replenishmentDayNumberPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.REPLENISHMENT_DAY_NUMBER,
    FieldStateKey.REPLENISHMENT_DAY_NUMBER,
  );
  const replenishmentFrequencyTypePermProps = useGetPermissionToEditOverridableField(
    PermissionKey.REPLENISHMENT_FREQUENCY_TYPE,
    FieldStateKey.REPLENISHMENT_FREQUENCY_TYPE,
  );
  const externalReplenishmentAccountMethodPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.EXTERNAL_REPLENISHMENT_ACCOUNT_METHOD,
    FieldStateKey.EXTERNAL_REPLENISHMENT_ACCOUNT_METHOD,
  );
  const automatedOrgLockEnabledPermProps = useGetPermissionToEditOverridableField(
    PermissionKey.AUTOMATED_ORG_LOCK_ENABLED,
    FieldStateKey.AUTOMATED_ORG_LOCK_ENABLED,
  );
  const { viewMode } = useSetupEmployerMode();

  const getDefaultValue = useCallback((value?: string) => {
    const bankId = banks.find((bank) => bank.id === value)?.id;

    if (bankId) return bankId;

    if (formattedData?.normalProperties?.externalAccountEnable) return NON_ASSIGNABLE_VALUE;

    return '';
  }, [banks, formattedData]);

  const options = banks.map((bank) => ({
    key: bank.id,
    value: bank.description,
  }));

  const getDayOfWeek = useCallback((numberOfDay?: number) => (numberOfDay ? weeklyOptions[numberOfDay - 1] : ''), []);
  const replenishmentDayNumber = formattedData?.overridableProperties?.replenishmentDayNumber;

  const frequencyType = useMemo(
    () => currentValues?.replenishmentFrequencyType,
    [currentValues],
  );

  const frequencyTypeFromResponse = useMemo(
    () => formattedData?.overridableProperties?.replenishmentFrequencyType,
    [formattedData],
  );

  const renderFrequencyDayField = useMemo(() => (
    frequencyType === FrequencyOptions.WEEKLY ? ({
      name: 'replenishmentDayOfWeek',
      label: 'Day of the week',
      type: FieldTypes.Dropdown,
      singleMode: true,
      showRequireIcon: true,
      placeholder: 'Select a day',
      defaultValue: frequencyTypeFromResponse === FrequencyOptions.WEEKLY
        ? getDayOfWeek(replenishmentDayNumber) : weeklyOptions[3],
      value: frequencyTypeFromResponse === FrequencyOptions.WEEKLY
        ? getDayOfWeek(replenishmentDayNumber) : weeklyOptions[3],
      options: weeklyOptions.map((option: string) => ({
        key: option,
        value: capitalizeFirstLetter(option),
      })),
      ...replenishmentDayNumberPermProps,
    }) : ({
      name: 'replenishmentCustomDay',
      label: 'Custom',
      type: FieldTypes.Number,
      lowerHelpText: CUSTOM_DAY_FIELD_HINT,
      placeholder: 'Enter custom day number',
      defaultValue: frequencyTypeFromResponse === FrequencyOptions.MONTHLY
        ? replenishmentDayNumber : '',
      value: frequencyTypeFromResponse === FrequencyOptions.MONTHLY
        ? replenishmentDayNumber : '',
      validator: yup.string().test('range', CUSTOM_DAY_ERROR_TEXT, (val) => {
        const customDayNumber = _.toNumber(val);
        return customDayNumber > 0 && customDayNumber <= 31;
      }).required(REQUIRED_TEXT),
      ...replenishmentDayNumberPermProps,
    })
  ), [
    frequencyType,
    getDayOfWeek,
    frequencyTypeFromResponse,
    replenishmentDayNumber,
    replenishmentDayNumberPermProps,
  ]);

  const isNeedToShowFrequencyDayField = useMemo(() => frequencyType
  && frequencyType !== FrequencyOptions.DAILY,
  [frequencyType]);

  const methodsOptions = useMemo(() => {
    const options = methods.map((option) => ({
      label: option.description,
      value: option.name,
    }));
  
    return options;
  }, [methods]);

  const fields = useMemo(() => {
    if (isLoading) return [];

    return [
      {
        name: 'fundingReplenishmentMethod',
        type: FieldTypes.Radio,
        disabled: viewMode,
        parentValueFn: (val) => !!val,
        label: 'Method',
        validator: yup.string().test({
          test: (val) => !!(val || formattedData?.overridableProperties?.externalReplenishmentAccountMethod),
          message: REQUIRED_TEXT,
        }),
        options: methodsOptions,
        value: formattedData?.overridableProperties?.externalReplenishmentAccountMethod,
        showRequireIcon: true,
        ...externalReplenishmentAccountMethodPermProps,
      },
      ...(currentValues?.fundingReplenishmentMethod === AccountMethods.PULL_FUNDS ? [{
        name: 'fundingReplenishmentBank',
        type: FieldTypes.Dropdown,
        disabled: viewMode,
        showRequireIcon: true,
        validator: activateMode ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING,
        placeholder,
        singleMode: true,
        label: 'Bank account',
        options,
        defaultValue: getDefaultValue(formattedData?.normalProperties?.externalReplenishmentAccount),
        value: getDefaultValue(formattedData?.normalProperties?.externalReplenishmentAccount),
        ...externalReplenishmentAccountMethodPermProps,
      },
      ] : [
        {
          name: 'accountToPushTo',
          type: FieldTypes.Node,
          label: 'Account to push to',
          value: (
            <AccountToPushToField
              accountNumber={notionalAccount}
              routingNumber={routingNumber}
              testId="funding-replenishment"
              accountPlaceholder={ACCOUNT_PLACEHOLDER}
            />
          ),
          ...externalReplenishmentAccountMethodPermProps,
        },
      ]),
      {
        name: 'replenishmentFrequencyType',
        label: 'Frequency',
        type: FieldTypes.Dropdown,
        singleMode: true,
        placeholder: 'Select option',
        defaultValue: frequencyTypeFromResponse || '',
        value: frequencyTypeFromResponse || '',
        options: Object.values(FrequencyOptions).map((option: string) => ({
          key: option,
          value: capitalizeFirstLetter(option),
        })),
        ...replenishmentFrequencyTypePermProps,
      },
      ...(isNeedToShowFrequencyDayField ? [renderFrequencyDayField] : []),
      {
        name: 'automatedOrgLockEnabled',
        type: FieldTypes.Radio,
        label: (
          <Box flex={{ grow: 1 }}>
            <Box direction="row" height={{ max: '20px' }} margin={{ bottom: '4px' }}>
              <Inscription
                color="danger"
                margin={{ right: '3px' }}
              >
                *
              </Inscription>
              <Inscription weight="bold" color="textBody" lineHeight="20px">
                Automatic Org Lock
              </Inscription>
            </Box>
            <Box>
              <Inscription weight={500} color="textSecondary" size="small" lineHeight="18px">
                Automatically lock claim spend on notional accounts if Pre-fund or Replenishment payments failed.
              </Inscription>
            </Box>
          </Box>
        ),
        options: automatedOrgLockOptions,
        value: formattedData?.overridableProperties?.automatedOrgLockEnabled,
        defaultValue: formattedData?.overridableProperties?.automatedOrgLockEnabled,
        validator: yup.boolean().required(REQUIRED_TEXT),
        hasHorizontalLine: true,
        ...automatedOrgLockEnabledPermProps,
      },
      // {
      //   name: 'fundingReplenishmentBank',
      //   type: FieldTypes.Dropdown,
      //   disabled: viewMode,
      //   validator: activateMode ? yup.string().required(REQUIRED_TEXT) : yup.string(),
      //   placeholder,
      //   singleMode: true,
      //   label: 'Bank account',
      //   options,
      //   subFields: [
      //     {
      //       name: 'fundingReplenishmentMethod',
      //       type: FieldTypes.Radio,
      //       disabled: viewMode,
      //       parentValueFn: (val) => !!val,
      //       label: 'Method',
      //       validator: yup.string().test({
      //         test: (val) => !!(val || formattedData?.overridableProperties?.externalReplenishmentAccountMethod),
      //         message: REQUIRED_TEXT,
      //       }),
      //       options: methods.map((option) => ({
      //         label: option.description,
      //         value: option.name,
      //       })),
      //       value: formattedData?.overridableProperties?.externalReplenishmentAccountMethod,
      //       showRequireIcon: true,
      //       ...externalReplenishmentAccountMethodPermProps,
      //     },
      //     {
      //       name: 'replenishmentFrequencyType',
      //       label: 'Frequency',
      //       type: FieldTypes.Dropdown,
      //       showRequireIcon: true,
      //       singleMode: true,
      //       placeholder: 'Select option',
      //       defaultValue: frequencyTypeFromResponse || '',
      //       value: frequencyTypeFromResponse || '',
      //       hasHorizontalLine: frequencyType === FrequencyOptions.DAILY,
      //       options: Object.values(FrequencyOptions).map((option: string) => ({
      //         key: option,
      //         value: capitalizeFirstLetter(option),
      //       })),
      //       ...replenishmentFrequencyTypePermProps,
      //     },
      //   ],
      //   areSubFieldsIncluded: true,
      //   defaultValue: getDefaultValue(formattedData?.normalProperties?.externalReplenishmentAccount),
      //   value: getDefaultValue(formattedData?.normalProperties?.externalReplenishmentAccount),
      // },
      // ...(isNeedToShowFrequencyDayField ? [renderFrequencyDayField] : []),
    ] as Field[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    banks,
    viewMode,
    methods,
    formattedData,
    currentValues,
    getDefaultValue,
    isNeedToShowFrequencyDayField,
    renderFrequencyDayField,
    activateMode,
    isLoading,
    automatedOrgLockEnabledPermProps,
    routingNumber,
  ]);

  const formattedFields = useHideFields(fields);
  return formattedFields;
};

export default useFundingReplenishmentFields;
